import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { api } from "@/lib/api"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Capitalize a provider ID into a display name (e.g. "azure_openai" → "Azure OpenAI"). */
export function formatProviderName(provider: string): string {
  if (!provider) return 'Unknown';
  if (provider.toLowerCase() === 'openai') return 'OpenAI';
  if (provider.toLowerCase() === 'azure_openai') return 'Azure OpenAI';
  if (provider.toLowerCase() === 'google_deepmind') return 'Google DeepMind';
  return provider
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export interface ProviderOption {
  id: string;
  name: string;
  models: string[];
}

type RawProviderModel = string | { id?: string; name?: string };

interface RawProvider {
  id?: string | number;
  provider?: string | number;
  name?: string | number;
  models?: RawProviderModel[];
}

/** Fetch configured providers from /api/llm/providers and return normalized ProviderOption[]. */
export async function fetchConfiguredProviders(_apiKey?: string): Promise<ProviderOption[]> {
  try {
    const responseData = await api.get('/llm/providers', true);
    const providersData = responseData.providers || [];
    if (!Array.isArray(providersData) || providersData.length === 0) return [];
    return providersData.map((p: RawProvider) => {
      const id = (p.id || p.provider || p.name || 'unknown').toString().toLowerCase();
      const models = Array.isArray(p.models)
        ? p.models.map((m: RawProviderModel) => typeof m === 'string' ? m : m.id || m.name || String(m))
        : [];
      return { id, name: formatProviderName(id), models };
    });
  } catch {
    return [];
  }
}
